// Library methods
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Components
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import TableCard from "src/components/shared/tables/TableCard/TableCard";
import { AlertFileIcon, DownloadIcon, ViewFileIcon } from "src/assets/icons";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  getSurfaceTokens,
  noHoverBackgroundSx,
} from "src/styles/mui/muiStylesHelper";

// Hooks
import useOnDemandProgressionReportGeneration from "src/hooks/reports/useOnDemandProgressionReportGeneration";

// Types
import { ProgressionAnalysisType } from "src/services/exams/types";

// Constants
import { DEFAULT_REPORT_TYPE } from "src/constants/enums";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type ExtendedProgressionAnalysisType = ProgressionAnalysisType & {
  patient: { id: string; firstName: string; lastName: string };
  firstName: string;
  lastName: string;
  formattedCreationDate: string;
};

type RecentProgressionAnalysisCardType = {
  progressionAnalysis: ExtendedProgressionAnalysisType;
};

const RecentProgressionAnalysisCard = ({
  progressionAnalysis,
}: RecentProgressionAnalysisCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const navigate = useNavigate();

  const theme = useTheme();
  const { elevatedSurface } = getSurfaceTokens(theme);
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const isAnalysisValid = useMemo(
    () => progressionAnalysis.state === "Valid",
    [progressionAnalysis.state]
  );

  // TODO: enable progression downloads from the card
  const canDownload = useMemo(
    () => false,
    // isAnalysisValid &&
    // Boolean(
    //   progressionAnalysis.exams?.length > 0 ||
    //     progressionAnalysis.externalExams?.length > 0
    // ),
    [
      // isAnalysisValid,
      // progressionAnalysis.exams,
      // progressionAnalysis.externalExams,
    ]
  );

  const { downloadFile } = useOnDemandProgressionReportGeneration({
    patientId: progressionAnalysis?.patient?.id,
    analysisId: progressionAnalysis?.id,
  });

  const fileType = useMemo(
    () => clinicInfo?.settings?.defaultReportType ?? DEFAULT_REPORT_TYPE,
    [clinicInfo?.settings?.defaultReportType]
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const recentlyCompletedHref = `/patients/${progressionAnalysis?.patient?.id}/progression-analysis/${progressionAnalysis?.id}`;

  const formattedPatientName = useMemo(
    () => `${progressionAnalysis?.firstName} ${progressionAnalysis?.lastName}`,
    [progressionAnalysis?.firstName, progressionAnalysis?.lastName]
  );

  return (
    <TableCard
      key={progressionAnalysis.id}
      isSelectedForViewing={false}
      isSelectedForDeletion={false}
      onClick={(_) => {}}
      hasHoverState={false}
      variant="dark"
      backgroundColorOverride={
        theme.palette.mode === "dark" ? elevatedSurface : undefined
      }
    >
      <Grid container item xs={12} alignItems="center">
        <Grid container item xs={10} display="flex" alignItems="center">
          <Grid
            item
            xs={12}
            sm={4}
            role="button"
            sx={{
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={(e) => {
              // open page in new tab if the meta key or ctrl key is pressed
              if (e.metaKey || e.ctrlKey) {
                localStorage.setItem(
                  "patient_id",
                  progressionAnalysis?.patient?.id
                );
                window.open("/patients", "_blank");
                return;
              }

              navigate("/patients", {
                state: { patient_id: progressionAnalysis?.patient?.id },
              });
            }}
          >
            <Tooltip title={formattedPatientName}>
              <Typography
                sx={{
                  maxWidth: "fit-content",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  paddingRight: "16px",
                }}
              >
                {smallerThanSm && (
                  <span style={{ fontWeight: 600 }}>{t("Patient")}: </span>
                )}
                {formattedPatientName}
              </Typography>
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={5}>
            <Tooltip title={t(progressionAnalysis?.eye)}>
              <Typography
                sx={{
                  maxWidth: "fit-content",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  paddingRight: "16px",
                }}
              >
                {smallerThanSm && (
                  <span style={{ fontWeight: 600 }}>{t("Eye")}: </span>
                )}
                {t(progressionAnalysis?.eye)}
              </Typography>
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Tooltip title={progressionAnalysis?.formattedCreationDate}>
              <Typography
                sx={{
                  maxWidth: "fit-content",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  paddingRight: "16px",
                }}
              >
                {smallerThanSm && (
                  <span style={{ fontWeight: 600 }}>{t("Created")}: </span>
                )}
                {progressionAnalysis?.formattedCreationDate}
              </Typography>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid
          item
          xs={2}
          sx={{
            display: "flex",
            height: "100%",
            justifyContent: "flex-end",
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          {canDownload && (
            <Tooltip title={t(`Download {{ fileType }} Report`, { fileType })}>
              <span>
                <IconButton
                  onClick={async () => {
                    setIsDownloading(true);
                    await downloadFile();
                    setIsDownloading(false);
                  }}
                  sx={{
                    py: { xs: "8px", sm: "0px" },
                    color: "inherit",
                    "&.Mui-disabled": {
                      color: "inherit",
                      opacity: "0.5",
                    },
                    ...noHoverBackgroundSx,
                  }}
                  disabled={isDownloading}
                >
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {isAnalysisValid ? (
            <Tooltip title={t("View Results")}>
              <span>
                <a
                  href={recentlyCompletedHref}
                  style={{ textDecoration: "none", color: "inherit" }}
                  onClick={(e) => {
                    e.preventDefault();

                    // open report in new tab if the meta key or ctrl key is pressed
                    if (e.metaKey || e.ctrlKey)
                      window.open(recentlyCompletedHref, "_blank");
                    // else open the report in the same tab
                    else navigate(recentlyCompletedHref);
                  }}
                >
                  <IconButton
                    sx={{
                      py: { xs: "8px", sm: "0px" },
                      color: "inherit",
                      ...noHoverBackgroundSx,
                    }}
                  >
                    <ViewFileIcon />
                  </IconButton>
                </a>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={t("Invalid Report")}>
              <span>
                <IconButton
                  sx={{
                    py: { xs: "8px", sm: "0px" },
                    opacity: 0.5,
                    cursor: "default",
                    color: "inherit",
                    ...noHoverBackgroundSx,
                  }}
                >
                  <AlertFileIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Grid>
      </Grid>
    </TableCard>
  );
};

export default RecentProgressionAnalysisCard;
